/**
 * ============================================================================
 * VENDOR HELPERS - Vendor Data Formatting & Display Utilities
 * ============================================================================
 * 
 * PURPOSE:
 * Provides utility functions for vendor data formatting, compatibility
 * scoring, and vendor profile modal preparation. Ensures consistent
 * vendor display across Smart Vendor Matching and Shortlisting Assistant.
 * 
 * KEY FUNCTIONS:
 * 
 * 1. getCompatibilityColor(score)
 *    - Returns Tailwind classes for compatibility badge color
 *    - 80+: Green (Excellent), 65+: Blue (Great), 50+: Yellow (Good), <50: Red (Moderate)
 * 
 * 2. getCompatibilityLabel(score)
 *    - Returns human-readable compatibility label
 *    - Used in vendor match cards
 * 
 * 3. formatVendorForModal(vendor)
 *    - Formats vendor data for VendorProfileModal component
 *    - Extracts services from nested highlights array
 *    - Normalizes vendor object structure
 *    - Handles missing/optional fields gracefully
 * 
 * COMPATIBILITY SCORING:
 * - 80-100: Excellent Match (Green) - Highly recommended
 * - 65-79: Great Match (Blue) - Strong compatibility
 * - 50-64: Good Match (Yellow) - Decent fit
 * - 0-49: Moderate Match (Red) - Consider alternatives
 * 
 * USAGE:
 * - SmartVendorMatching: Display match scores and vendor cards
 * - VendorShortlistingAssistant: Format vendor profiles
 * - VendorCard component: Compatibility badges
 * - VendorProfileModal: Data preparation
 * 
 * ============================================================================
 */

// ============================================================================
// COMPATIBILITY SCORING HELPERS
// ============================================================================

/**
 * Get Tailwind color classes for compatibility score badge
 * 
 * COLOR CODING SYSTEM:
 * - 80-100: Green (Excellent Match) - Highly recommended
 * - 65-79: Blue (Great Match) - Strong compatibility
 * - 50-64: Yellow (Good Match) - Decent fit
 * - 0-49: Red (Moderate Match) - Consider alternatives
 * 
 * USAGE:
 * - SmartVendorMatching: Display match scores
 * - VendorCard component: Compatibility badges
 * 
 * @param {Number} score - Compatibility score (0-100)
 * @returns {String} Tailwind CSS classes for text and background
 */
export const getCompatibilityColor = (score) => {
  if (score >= 80) return 'text-green-600 bg-green-100';   // Excellent Match
  if (score >= 65) return 'text-blue-600 bg-blue-100';     // Great Match
  if (score >= 50) return 'text-yellow-600 bg-yellow-100'; // Good Match
  return 'text-red-600 bg-red-100';                        // Moderate Match
};

/**
 * Get human-readable label for compatibility score
 * 
 * LABELS:
 * - 80+: "Excellent Match"
 * - 65-79: "Great Match"
 * - 50-64: "Good Match"
 * - 0-49: "Moderate Match"
 * 
 * @param {Number} score - Compatibility score (0-100)
 * @returns {String} Human-readable label
 */
export const getCompatibilityLabel = (score) => {
  if (score >= 80) return 'Excellent Match';
  if (score >= 65) return 'Great Match';
  if (score >= 50) return 'Good Match';
  return 'Moderate Match';
};

// ============================================================================
// VENDOR DATA FORMATTING
// ============================================================================

/**
 * Format vendor data for VendorProfileModal component
 * 
 * WHAT IT DOES:
 * 1. Extracts services from nested highlights array
 * 2. Normalizes vendor object structure
 * 3. Handles missing/optional fields gracefully
 * 4. Returns standardized format for modal display
 * 
 * HANDLES MULTIPLE DATA FORMATS:
 * - Backend API format (price_range, highlights array)
 * - Frontend mock format (price, highlights nested)
 * - Ensures consistent modal display regardless of source
 * 
 * USAGE:
 * - SmartVendorMatching: Format vendor for profile modal
 * - VendorShortlistingAssistant: Format vendor for profile modal
 * 
 * @param {Object} vendor - Raw vendor data from API or mock
 * @returns {Object} Formatted vendor object for modal
 */
export const formatVendorForModal = (vendor) => {
  // ========================================================================
  // STEP 1: EXTRACT SERVICES FROM HIGHLIGHTS
  // ========================================================================
  // Highlights can be nested array or flat array depending on source
  let services = [];
  if (vendor.highlights && vendor.highlights.length > 0) {
    // Check if highlights is nested: [[service1, service2]] vs [service1, service2]
    services = Array.isArray(vendor.highlights[0]) ? vendor.highlights[0] : vendor.highlights;
  }
  
  // ========================================================================
  // STEP 2: FORMAT VENDOR DATA FOR MODAL
  // ========================================================================
  // Normalize field names and provide defaults for missing data
  return {
    id: vendor.id,
    name: vendor.name,
    category: vendor.category || 'Wedding Services',  // Default category if missing
    tagline: vendor.tagline,
    description: vendor.description,
    rating: vendor.rating || 4.5,                      // Default rating if missing
    reviews: vendor.reviews || 0,                      // Default 0 reviews if missing
    location: vendor.location,
    price: vendor.price_range || vendor.price || 'Contact for pricing',  // Handle multiple price field names
    price_range: vendor.price_range || vendor.price || 'Contact for pricing',
    style: vendor.style || 'Various styles available',
    services: services,
    highlights: services
  };
};

export const vendorCategories = [
  { name: 'Venue', icon: '🏛️', color: 'from-rose-100 to-pink-200' },
  { name: 'Catering', icon: '🍽️', color: 'from-champagne-100 to-gold-200' },
  { name: 'Photography', icon: '📸', color: 'from-purple-100 to-lavender-200' },
  { name: 'Flowers', icon: '🌸', color: 'from-pink-100 to-rose-200' },
  { name: 'Music', icon: '🎵', color: 'from-blue-100 to-indigo-200' },
  { name: 'Attire', icon: '👗', color: 'from-mauve-100 to-purple-200' },
  { name: 'Transportation', icon: '🚗', color: 'from-teal-100 to-cyan-200' },
  { name: 'Planning', icon: '📋', color: 'from-blush-100 to-pink-200' }
];
